import React, { useMemo, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  LinearProgress,
  MenuItem,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import ArrowDropDown from '@mui/icons-material/ArrowDropDown'
import ViewColumn from '@mui/icons-material/ViewColumn'
import dayjs from 'dayjs'
import { visuallyHidden } from '@mui/utils'
import { useTranslation } from 'react-i18next'
import ColumnMenu from './ColumnMenu'

/**
 * A table whose columns carry their own filter, sort and visibility.
 *
 * The parcel list and Location Control had the same table twice, one of them much better than the
 * other. This is that table: a caller brings column descriptors, rows, and the filter and sort it
 * already keeps, and gets the headings-as-menus, the chips that say what is on, the column picker
 * and the drag-to-reorder without owning any of it.
 *
 * What a column may declare:
 *   id, label, width, type, render(row), sortable, sortBy, hidden, defaultHidden,
 *   filter: { kind: 'text' | 'select' | 'multiSelect' | 'dateRange', path, options, translate }
 *
 * `filter.translate` is a key prefix and may name a namespace, e.g. 'parcels:statuses', because
 * this component does not know which namespace a caller's values live in.
 */

/** Filters live at a path because some of them are nested, e.g. location.country. */
export const valueAt = (filters, path) =>
  path
    .split('.')
    .reduce(
      (value, key) =>
        value === undefined || value === null ? undefined : value[key],
      filters
    )

export const withValueAt = (filters, path, value) => {
  const [key, ...rest] = path.split('.')
  if (!rest.length) return { ...filters, [key]: value }
  return {
    ...filters,
    [key]: withValueAt(filters[key] || {}, rest.join('.'), value),
  }
}

/**
 * Whether a filter is actually on. Callers differ: some clear to undefined, some to an empty
 * string, and a multi-select clears to an empty array. None of those are a filter.
 */
const isSet = (value) =>
  value !== undefined &&
  value !== null &&
  value !== '' &&
  !(Array.isArray(value) && !value.length)

/** What the server sorts a column by, which is not always the column's own id. */
export const sortKeyOf = (column) => column.sortBy || column.id

const readStored = (key) => {
  try {
    const stored = JSON.parse(window.localStorage.getItem(key))
    return Array.isArray(stored) ? stored : null
  } catch {
    return null
  }
}

const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // a browser that will not store it still works, it just forgets between visits
  }
}

/**
 * The registry order with the stored one laid over it. Ids the store does not know about keep
 * their registry position, so a column added later appears rather than vanishing.
 */
const applyOrder = (columns, order) => {
  if (!order) return columns
  const known = order.filter((id) => columns.some((column) => column.id === id))
  const rest = columns.filter((column) => !known.includes(column.id))
  return [
    ...known.map((id) => columns.find((column) => column.id === id)),
    ...rest,
  ]
}

/** One filter in words, for the chip that says it is on. */
const describeFilter = (column, value, t) => {
  const { filter } = column
  const label = (option) =>
    filter.translate ? t(`${filter.translate}.${option}`) : option

  if (filter.kind === 'dateRange') {
    if (value?.type === 'relative' && value.value)
      return `${value.value} ${value.unit || ''}`.trim()
    const from = value?.fromDate ? dayjs(value.fromDate).format('L') : ''
    const to = value?.toDate ? dayjs(value.toDate).format('L') : ''
    return [from, to].filter(Boolean).join(' – ')
  }
  if (filter.kind === 'number') return `≥ ${value}`
  if (filter.kind === 'multiSelect') return (value || []).map(label).join(', ')
  if (filter.kind === 'select') {
    const option = (filter.options || []).find(
      (o) => (typeof o === 'string' ? o : o.value) === value
    )
    if (option && typeof option !== 'string') return option.label
    return label(value)
  }
  return String(value)
}

export default function ColumnTable({
  columns,
  rows,
  storageKey,
  filters = {},
  onFilter,
  onClearFilters,
  sorting = {},
  onSort,
  getRowKey = (row) => row._id,
  onRowClick,
  isRowSelected,
  emptyLabel,
  toolbar,
  loading = false,
}) {
  const { t } = useTranslation('common')
  const [columnOrder, setColumnOrder] = useState(() =>
    readStored(`${storageKey}ColumnOrder`)
  )
  const [hiddenColumns, setHiddenColumns] = useState(() =>
    readStored(`${storageKey}HiddenColumns`)
  )
  const [dragging, setDragging] = useState(null)
  const [menu, setMenu] = useState(null)
  const [picker, setPicker] = useState(null)

  // No stored choice means the columns decide for themselves
  const hidden = useMemo(
    () =>
      hiddenColumns ??
      columns
        .filter((column) => column.defaultHidden)
        .map((column) => column.id),
    [hiddenColumns, columns]
  )

  const ordered = applyOrder(columns, columnOrder)
  const shown = ordered.filter(
    (column) => !column.hidden && !hidden.includes(column.id)
  )

  const activeFilters = ordered
    .filter(
      (column) => column.filter && isSet(valueAt(filters, column.filter.path))
    )
    .map((column) => ({ column, value: valueAt(filters, column.filter.path) }))

  const toggleColumn = (id) => {
    const next = hidden.includes(id)
      ? hidden.filter((column) => column !== id)
      : [...hidden, id]
    writeStored(`${storageKey}HiddenColumns`, next)
    setHiddenColumns(next)
  }

  // Dropping one heading on another puts it in that place; the whole order is then remembered.
  const moveColumn = (fromId, toId) => {
    if (!fromId || fromId === toId) return
    const ids = ordered.map((column) => column.id)
    const next = ids.filter((id) => id !== fromId)
    next.splice(next.indexOf(toId), 0, fromId)
    writeStored(`${storageKey}ColumnOrder`, next)
    setColumnOrder(next)
  }

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
          px: 1,
          pb: 0.5,
        }}
      >
        <Button
          size="small"
          startIcon={<ViewColumn fontSize="small" />}
          onClick={(e) => setPicker(e.currentTarget)}
        >
          {t('columns')}
        </Button>
        {activeFilters.map(({ column, value }) => (
          <Chip
            key={column.id}
            size="small"
            variant="outlined"
            label={`${column.label}: ${describeFilter(column, value, t)}`}
            onDelete={() => onFilter(column.filter.path, undefined, column)}
            onClick={(e) => setMenu({ column, anchorEl: e.currentTarget })}
          />
        ))}
        {activeFilters.length && onClearFilters ? (
          <Button size="small" onClick={onClearFilters}>
            {t('clearFilters', { count: activeFilters.length })}
          </Button>
        ) : null}
        {toolbar}
        <Popover
          open={Boolean(picker)}
          anchorEl={picker}
          onClose={() => setPicker(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <Box sx={{ p: 1, minWidth: 220 }}>
            {ordered
              .filter((column) => !column.hidden)
              .map((column) => (
                <MenuItem
                  key={column.id}
                  dense
                  onClick={() => toggleColumn(column.id)}
                >
                  <Checkbox
                    size="small"
                    checked={!hidden.includes(column.id)}
                    sx={{ p: 0.5, mr: 1 }}
                  />
                  {column.label}
                </MenuItem>
              ))}
          </Box>
        </Popover>
      </Box>

      {/* Sorting by a column the server has to compute can take seconds; say so rather than
          leaving the old rows sitting there looking like the answer. */}
      <Box sx={{ height: 4 }}>
        {loading ? <LinearProgress color="iboxen" /> : null}
      </Box>

      <TableContainer
        sx={{
          pb: 0.5,
          opacity: loading ? 0.5 : 1,
          transition: 'opacity 150ms',
          pointerEvents: loading ? 'none' : undefined,
        }}
      >
        <Table size="small" sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow sx={{ bgcolor: 'rgba(241, 241, 241, 0.745)' }}>
              {shown.map((column) => {
                const active =
                  column.filter && isSet(valueAt(filters, column.filter.path))
                return (
                  <TableCell
                    component="th"
                    key={column.id}
                    align={column.type === Number ? 'right' : 'left'}
                    width={column.width}
                    draggable
                    onDragStart={() => setDragging(column.id)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => {
                      moveColumn(dragging, column.id)
                      setDragging(null)
                    }}
                    onDragEnd={() => setDragging(null)}
                    sx={{
                      cursor: 'grab',
                      userSelect: 'none',
                      opacity: dragging === column.id ? 0.4 : 1,
                    }}
                    onClick={(e) =>
                      setMenu({ column, anchorEl: e.currentTarget })
                    }
                  >
                    <Box
                      component="span"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontWeight: active ? 'bold' : undefined,
                      }}
                    >
                      {column.label}
                      {sorting.orderBy === sortKeyOf(column) ? (
                        <>
                          {sorting.order === 'asc' ? '↑' : '↓'}
                          <Box component="span" sx={visuallyHidden}>
                            {sorting.order === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        </>
                      ) : null}
                      {active ? '•' : null}
                      <ArrowDropDown fontSize="small" />
                    </Box>
                  </TableCell>
                )
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, i) => (
              <TableRow
                hover
                key={getRowKey(row)}
                selected={isRowSelected ? isRowSelected(row) : false}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  cursor: onRowClick ? 'pointer' : undefined,
                  bgcolor: i % 2 && 'rgba(241, 241, 241, 0.745)',
                }}
              >
                {shown.map((column) => (
                  <TableCell
                    key={`${getRowKey(row)}-${column.id}`}
                    align={column.type === Number ? 'right' : 'left'}
                    sx={column.sx}
                  >
                    {typeof column.render === 'function'
                      ? column.render(row)
                      : row[column.selector || column.id]}
                  </TableCell>
                ))}
              </TableRow>
            ))}
            {!rows.length && emptyLabel && !loading ? (
              <TableRow>
                <TableCell colSpan={shown.length} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {emptyLabel}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>

      <ColumnMenu
        column={menu?.column}
        anchorEl={menu?.anchorEl}
        onClose={() => setMenu(null)}
        sorting={sorting}
        onSort={(order) => {
          onSort({
            orderBy: order ? sortKeyOf(menu.column) : undefined,
            order,
          })
          setMenu(null)
        }}
        value={
          menu?.column?.filter
            ? valueAt(filters, menu.column.filter.path)
            : undefined
        }
        onFilter={(value) =>
          onFilter(menu.column.filter.path, value, menu.column)
        }
        onHide={() => toggleColumn(menu.column.id)}
      />
    </>
  )
}
